import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import type { DILUTION_CALCULATOR_STRINGS } from "@/components/tools/dilution-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { DilutionOutcome } from "@/lib/tool-kit/chemistry/dilution";
import { formatPlain, formatSig } from "@/lib/tool-kit/chemistry/format";
import type { DilutionTerm } from "@/lib/tool-kit/chemistry/solutions";
import {
  findConcentrationUnit,
  findUnit,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";

type Strings = typeof DILUTION_CALCULATOR_STRINGS;
type Solved = Extract<DilutionOutcome, { ok: true }>;

export interface UnitIds {
  c1: string;
  c2: string;
  v1: string;
  v2: string;
}

const FORMULAS: Record<DilutionTerm, keyof Strings> = {
  c1: "rearrangedC1",
  v1: "rearrangedV1",
  c2: "rearrangedC2",
  v2: "rearrangedV2",
};

const WITH_VALUES: Record<DilutionTerm, keyof Strings> = {
  c1: "substitutedC1",
  v1: "substitutedV1",
  c2: "substitutedC2",
  v2: "substitutedV2",
};

export function DilutionAnswer({
  unknown,
  r,
  typed,
  texts,
  units,
  sig,
  strings,
}: {
  /** The raw entries, echoed in the working when no unit conversion changed them. */
  texts: Record<DilutionTerm, string>;
  unknown: DilutionTerm;
  r: Solved;
  /** What the reader typed, for echoing unit conversions. */
  typed: { c2: number | null; v1: number | null };
  units: UnitIds;
  sig: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const cSymbol = findConcentrationUnit(r.cUnit).symbol;
  const vSymbol = findUnit(VOLUME, r.vUnit).symbol;
  const answerSymbol =
    unknown === "c1" || unknown === "c2"
      ? findConcentrationUnit(r.answerUnit).symbol
      : findUnit(VOLUME, r.answerUnit).symbol;
  const labels: Record<DilutionTerm, string> = {
    c1: strings.answerC1,
    v1: strings.answerV1,
    c2: strings.answerC2,
    v2: strings.answerV2,
  };
  const answerText = `${formatSig(fmt, r.answer, sig)} ${answerSymbol}`;
  const g = sig + 1;
  const conversions: string[] = [];
  if (units.c2 !== units.c1 && typed.c2 !== null) {
    conversions.push(
      t(strings.convertLine, {
        value: formatPlain(fmt, typed.c2),
        unit: findConcentrationUnit(units.c2).symbol,
        converted: formatPlain(fmt, r.c2),
        target: cSymbol,
      })
    );
  }
  if (units.v1 !== units.v2 && typed.v1 !== null) {
    conversions.push(
      t(strings.convertLine, {
        value: formatPlain(fmt, typed.v1),
        unit: findUnit(VOLUME, units.v1).symbol,
        converted: formatPlain(fmt, r.v1),
        target: vSymbol,
      })
    );
  }
  const unconverted: Record<DilutionTerm, boolean> = {
    c1: true,
    v2: true,
    c2: units.c2 === units.c1,
    v1: units.v1 === units.v2,
  };
  const shown = (term: DilutionTerm): string =>
    term !== unknown && unconverted[term] && texts[term].trim() !== ""
      ? texts[term].trim()
      : formatPlain(fmt, r[term]);
  const substituted = t(strings[WITH_VALUES[unknown]], {
    c1: shown("c1"),
    c2: shown("c2"),
    v1: shown("v1"),
    v2: shown("v2"),
    cUnit: cSymbol,
    vUnit: vSymbol,
    result: formatSig(fmt, r[unknown], g),
  });
  const factor = formatSig(fmt, r.dilutionFactor, sig);
  const diluent = formatSig(fmt, r.diluent, sig);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard label={strings.dilutionFactor} value={`${factor}×`} />
        {r.diluent > 0 ? (
          <StatCard label={strings.diluent} value={`${diluent} ${vSymbol}`} />
        ) : null}
      </StatGrid>
      {r.concentrates ? <Notice>{strings.concentrates}</Notice> : null}
      {r.diluent < 0 ? <Notice>{strings.stockTooLarge}</Notice> : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.equation}</p>
        <p>{strings[FORMULAS[unknown]]}</p>
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        <p>
          {t(strings.factorLine, {
            c1: formatPlain(fmt, r.c1),
            c2: formatPlain(fmt, r.c2),
            factor,
          })}
        </p>
      </div>
      {r.diluent > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.recipeHeading}</h3>
          <p className="text-foreground text-sm leading-relaxed">
            {t(strings.recipe, {
              v1: formatSig(fmt, r.v1, sig),
              diluent,
              v2: formatSig(fmt, r.v2, sig),
              vUnit: vSymbol,
              parts: formatSig(fmt, r.dilutionFactor - 1, sig),
            })}
          </p>
        </div>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}
